<template>
	<DialogControl :attach="overlayTarget" v-model="open" fullscreen class="catchment-comparison-dialog">
		<v-card>
			<v-card-title class="headline">
				<v-icon>compare_arrows</v-icon>
				{{ t('CatchmentAreaComparison') }}
			</v-card-title>
			<v-card-text class="catchment-comparison-dialog__meta text-caption text-medium-emphasis">{{ referenceLabel }} · {{ comparedStores.length }} {{ t('ComparedStores').toLowerCase() }}</v-card-text>

			<v-card-text class="catchment-comparison-dialog__filters">
				<div class="search-box">
					<v-text-field v-model="searchText" :label="t('SearchProduct')" prepend-inner-icon="search" variant="outlined" density="compact" clearable hide-details class="catchment-comparison-dialog__search" />
					<v-switch v-model="gapsOnly" :label="t('OnlyShowGaps')" color="primary" hide-details density="compact" />
				</div>

				<template v-if="isTablet">
					<SearchSortByControl class="catchment-comparison-dialog__sort" :items="sortOptions" v-model:sort-by="sortBy" v-model:sort-order="sortOrder" />
				</template>
			</v-card-text>

			<v-card-text v-if="!displayedRows.length" class="text-center pa-8 text-disabled">{{ t('NoData') }}</v-card-text>
			<v-card-text v-else class="catchment-comparison-dialog__content">
				<AgGrid v-if="!isTablet" :row-data="displayedRows" :col-defs="columnDefs" :context="gridContext" :get-row-id="getRowId" :row-height="56" :rowGroupPanelShow="'never'" :auto-size-columns="false" group-display-type="groupRows" />
				<div v-else class="catchment-comparison-dialog__cards">
					<CatchmentComparisonProductCard v-for="row in sortedCardRows" :key="row.productId" :row="row" :stores="storeRowsFor(row)" :gap-tooltip="gapTooltip" />
				</div>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn text @click="open = false" data-close>{{ t('Close') }}</v-btn>
			</v-card-actions>
		</v-card>
	</DialogControl>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { formatValue } from '@/utils/core/format';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { getCatchmentAreaComparison } from '@/api/sales/catchmentArea';
import { STATEMENT_STATE } from '@/utils/domain/catchmentAreaComparison';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import SearchSortByControl from '@/components/controls/SearchSortByControl.vue';
import CatchmentTrendCellRenderer from './CatchmentTrendCellRenderer.vue';
import ProductCellRenderer from '@/components/agGrid/ProductCellRenderer.vue';
import ChipCellRenderer from '@/components/agGrid/ChipCellRenderer.vue';
import CatchmentStoreHeaderRenderer from './CatchmentStoreHeaderRenderer.vue';
import CatchmentComparisonProductCard from './CatchmentComparisonProductCard.vue';
import DialogControl from '@/components/controls/DialogControl.vue';

const STORE_BADGE_COLORS = ['secondary', 'tertiary', 'warning'];

function storeBadgeColor(index, isReference = false) {
	return isReference ? 'primary' : STORE_BADGE_COLORS[index % STORE_BADGE_COLORS.length];
}

function storeBadgeLetter(label) {
	const tokens = String(label || '')
		.split(/[\s.]+/)
		.filter(Boolean);
	const meaningful = tokens.find((token) => token.length > 1) || tokens[0] || '';
	return meaningful.charAt(0).toUpperCase();
}

const t = useTranslator();

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	overlayTarget: { type: [Object, null], default: null },
	context: { type: Object, default: null },
});
const emit = defineEmits(['update:modelValue']);

const open = computed({
	get: () => props.modelValue,
	set: (value) => emit('update:modelValue', value),
});

const rows = ref([]);
const referenceStore = ref(null);
const comparedStores = ref([]);
const searchText = ref('');
const gapsOnly = ref(false);
const sortBy = ref('label');
const sortOrder = ref('asc');

const isTablet = ref(deviceUtils.isTablet());
const onResize = () => {
	isTablet.value = deviceUtils.isTablet();
};

onMounted(() => window.addEventListener('resize', onResize));
onBeforeUnmount(() => window.removeEventListener('resize', onResize));

const referenceLabel = computed(() => referenceStore.value?.label || '');

watch(open, (isOpen) => {
	if (isOpen) loadComparison();
});

let loadRequestId = 0;

async function loadComparison() {
	const deliveryLocationId = props.context?.deliveryLocationId;
	if (deliveryLocationId == null) return;
	const requestId = ++loadRequestId;
	const result = await getCatchmentAreaComparison(deliveryLocationId, { localReferenceStatement: props.context?.localReferenceStatement || null });
	if (requestId !== loadRequestId) return;
	rows.value = result.rows;
	referenceStore.value = result.referenceStore;
	comparedStores.value = result.comparedStores;
}

function matchesSearch(row) {
	const query = searchText.value?.trim().toLowerCase();
	if (!query) return true;
	return [row.label, row.ean].some((field) =>
		String(field || '')
			.toLowerCase()
			.includes(query)
	);
}

// The two filters combine as a logical AND, order-independent (spec §7.3).
const displayedRows = computed(() => rows.value.filter((row) => matchesSearch(row) && (!gapsOnly.value || row.ecart)));

const sortOptions = computed(() => [
	{ id: 'label', name: t('LabelAZ') },
	{ id: 'ecart', name: t('GapsFirst') },
]);

const sortedCardRows = computed(() => {
	const direction = sortOrder.value === 'asc' ? 1 : -1;
	return [...displayedRows.value].sort((a, b) => {
		if (sortBy.value === 'ecart') return ((b.ecart ? 1 : 0) - (a.ecart ? 1 : 0)) * direction;
		return String(a.label).localeCompare(String(b.label)) * direction;
	});
});

const storesById = computed(() => new Map([referenceStore.value, ...comparedStores.value].filter(Boolean).map((store) => [store.id, store])));
const accountsById = computed(() => new Map((iframeDataBridge.getEcosVM()?.accounts?.() || []).map((account) => [account.id, account])));

function accountImageUidFor(store) {
	return accountsById.value.get(store?.accountId)?.imageUId || null;
}

function storeRowsFor(row) {
	return [
		{
			...row.referenceStore,
			deliveryLocationId: referenceStore.value?.id,
			label: referenceLabel.value,
			isReference: true,
			posType: referenceStore.value?.posType || '',
			badgeColor: storeBadgeColor(0, true),
			badgeLetter: storeBadgeLetter(referenceLabel.value),
			accountImageUid: accountImageUidFor(referenceStore.value),
		},
		...row.compared.map((entry, index) => {
			const store = storesById.value.get(entry.deliveryLocationId);
			return {
				...entry,
				isReference: false,
				posType: store?.posType || '',
				badgeColor: storeBadgeColor(index),
				badgeLetter: storeBadgeLetter(store?.label || entry.label),
				accountImageUid: accountImageUidFor(store),
			};
		}),
	];
}

const gapTooltip = computed(() => t('ProductInGap'));
const gridContext = computed(() => ({ gapTooltip: gapTooltip.value }));

function stateLabel(state) {
	if (state === STATEMENT_STATE.PRESENT) return t('Present');
	if (state === STATEMENT_STATE.ABSENT) return t('Absent');
	if (state === STATEMENT_STATE.OUT_OF_STOCK) return t('Out Of Stock');
	return '—';
}

function stateChipColor(state) {
	if (state === STATEMENT_STATE.PRESENT) return 'success';
	if (state === STATEMENT_STATE.ABSENT) return 'info';
	if (state === STATEMENT_STATE.OUT_OF_STOCK) return 'error';
	return undefined;
}

function entryFor(row, storeKey) {
	if (!row) return null;
	return storeKey === 'reference' ? row.referenceStore : row.compared.find((entry) => String(entry.deliveryLocationId) === storeKey);
}

// Category group rows carry no aggregated values at all — no getGroupRowAgg, and every store
// column opts out of the automatic sum/count AgGrid.vue's normalizeColDefs would otherwise infer
// (getDefaultAggFunc), so a category band stays empty instead of summing facings/prices/VMH.
const NO_AGGREGATION = { aggFunc: null, enableValue: false };

// Only the visited store's own cells turn red on a flagged row — the compared stores keep their
// normal background, since the red flag marks a gap on the reference store, not the whole product.
function gapCellClass(params) {
	return params.data?.ecart ? 'catchment-comparison-dialog__grid-cell--gap' : null;
}

function buildStoreColumnGroup(storeKey, store, { pinned = false, tinted = false, badgeColor = 'primary' } = {}) {
	const dense = { filter: false, suppressHeaderMenuButton: true };
	const cellClass = tinted ? gapCellClass : undefined;
	return {
		headerName: store.label,
		headerGroupComponent: CatchmentStoreHeaderRenderer,
		headerGroupComponentParams: { store, isReference: tinted, badgeColor, badgeLetter: storeBadgeLetter(store.label), accountImageUid: accountImageUidFor(store) },
		headerClass: tinted ? 'catchment-comparison-dialog__grid-header--reference' : undefined,
		children: [
			{
				headerName: t('Status'),
				colId: `${storeKey}_state`,
				pinned,
				width: 120,
				minWidth: 100,
				...NO_AGGREGATION,
				cellClass,
				valueGetter: (p) => entryFor(p.data, storeKey)?.state ?? null,
				valueFormatter: (p) => stateLabel(p.value),
				filterParams: { valueFormatter: (p) => stateLabel(p.value) },
				cellRenderer: ChipCellRenderer,
				cellRendererParams: (p) => ({ label: stateLabel(entryFor(p.data, storeKey)?.state ?? null), color: stateChipColor(entryFor(p.data, storeKey)?.state ?? null) }),
			},
			{ headerName: t('Facing'), colId: `${storeKey}_facing`, pinned, width: 140, minWidth: 70, ...NO_AGGREGATION, cellClass, cellDataType: 'number', valueGetter: (p) => entryFor(p.data, storeKey)?.facing ?? null, valueFormatter: (p) => (p.value == null ? '—' : p.value) },
			{ headerName: t('Price'), colId: `${storeKey}_price`, pinned, width: 90, minWidth: 80, ...dense, ...NO_AGGREGATION, cellClass, cellDataType: 'number', valueGetter: (p) => entryFor(p.data, storeKey)?.retailPrice ?? null, valueFormatter: (p) => formatValue(p.value, { decimals: 2 }) },
			{
				headerName: t('VMH'),
				colId: `${storeKey}_vmh`,
				pinned,
				width: 90,
				minWidth: 80,
				...dense,
				...NO_AGGREGATION,
				cellClass,
				cellDataType: 'number',
				valueGetter: (p) => entryFor(p.data, storeKey)?.vmh ?? null,
				cellRenderer: CatchmentTrendCellRenderer,
				cellRendererParams: (p) => ({ value: entryFor(p.data, storeKey)?.vmh ?? null, trend: entryFor(p.data, storeKey)?.vmhTrend ?? null }),
			},
			{
				headerName: t('VMM'),
				colId: `${storeKey}_vmm`,
				pinned,
				width: 90,
				minWidth: 80,
				...dense,
				...NO_AGGREGATION,
				cellClass,
				cellDataType: 'number',
				valueGetter: (p) => entryFor(p.data, storeKey)?.vmm ?? null,
				cellRenderer: CatchmentTrendCellRenderer,
				cellRendererParams: (p) => ({ value: entryFor(p.data, storeKey)?.vmm ?? null, trend: entryFor(p.data, storeKey)?.vmmTrend ?? null }),
			},
		],
	};
}

const columnDefs = computed(() => [
	{
		headerName: t('ProductTree'),
		field: 'category',
		colId: 'category',
		rowGroup: true,
		hide: true,
		// A product no relevé of the zone has ever surveyed and that carries no catalog category
		// still needs a group of its own — the em-dash placeholder used for every other missing
		// value here, rather than an unnamed band.
		valueGetter: (p) => p.data?.category || '—',
	},
	{
		headerName: t('Product'),
		field: 'label',
		colId: 'product',
		pinned: 'left',
		width: 250,
		minWidth: 160,
		...NO_AGGREGATION,
		cellRenderer: ProductCellRenderer,
		cellRendererParams: (p) => ({ subtitle: p.data?.ean, flag: !!p.data?.ecart, flagTooltip: p.context?.gapTooltip, imageThumbnailUid: p.data?.imageThumbnailUid }),
	},
	buildStoreColumnGroup('reference', referenceStore.value || { label: referenceLabel.value }, { pinned: 'left', tinted: true, badgeColor: storeBadgeColor(0, true) }),
	...comparedStores.value.map((store, index) => buildStoreColumnGroup(String(store.id), store, { badgeColor: storeBadgeColor(index) })),
]);

function getRowId(params) {
	return String(params.data.productId);
}
</script>

<style scoped lang="scss">
.catchment-comparison-dialog {
	&__meta {
		flex: none;
		height: auto;
		padding-top: 0;
	}

	&__filters {
		display: flex;
		flex: none;
		flex-direction: row;
		align-items: center;
		height: auto;
		gap: 16px;
		flex-wrap: wrap;
		padding-bottom: 8px !important;

		.v-select {
			max-width: unset;
		}

		.search-box {
			display: flex;
			gap: 16px;
			width: 100%;
		}
	}

	&__search {
		flex: 1 1 260px;
		max-width: 360px;
	}

	&__sort {
		flex: 1 1 auto;
		min-width: 160px;
	}

	&__content {
		flex: 1 1 auto;
		height: auto;
		min-height: 0;
		overflow: auto;
	}

	&__cards {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}

	:deep(.catchment-comparison-dialog__grid-header--reference) {
		background: rgba(var(--v-theme-primary), 0.1);
	}

	:deep(.ag-cell.catchment-comparison-dialog__grid-cell--gap) {
		background-color: rgba(var(--v-theme-error), 0.12);
	}
}
</style>
